/**
 * Real-time notification hook for back-office users
 * Uses polling to check for new applications and status changes
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";

export const useBackOfficeNotifications = (token, userRole, enabled = true) => {
  const [lastCheckTime, setLastCheckTime] = useState(null);
  const [newApplicationsCount, setNewApplicationsCount] = useState(0);
  const [escalatedCount, setEscalatedCount] = useState(0);
  const previousCountsRef = useRef({ pending: 0, recommended: 0, escalated: 0 });
  const isInitializedRef = useRef(false);

  const checkForUpdates = useCallback(async () => {
    if (!token || !enabled) return;

    try {
      const response = await axios.get(`${API}/backoffice/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const stats = response.data;
      const currentCounts = {
        pending: stats.by_status?.pending || 0,
        recommended: stats.by_status?.recommended || 0,
        escalated: stats.by_status?.escalated || 0,
      };

      // Skip notifications on first load
      if (!isInitializedRef.current) {
        previousCountsRef.current = currentCounts;
        isInitializedRef.current = true;
        setNewApplicationsCount(currentCounts.pending);
        setEscalatedCount(currentCounts.escalated);
        return;
      }

      // Check for new pending applications (for loan officers)
      if (currentCounts.pending > previousCountsRef.current.pending) {
        const newCount = currentCounts.pending - previousCountsRef.current.pending;
        if (userRole === "loan_officer" || userRole === "admin") {
          toast.info(`📋 ${newCount} new application(s) submitted`, {
            description: "Check the applications queue for review",
            duration: 6000,
          });
        }
      }

      // Check for recommended applications (for managers)
      if (currentCounts.recommended > previousCountsRef.current.recommended) {
        const newCount = currentCounts.recommended - previousCountsRef.current.recommended;
        if (userRole === "manager" || userRole === "admin") {
          toast.info(`📝 ${newCount} application(s) ready for manager review`, {
            description: "Loan officer recommendations are waiting",
            duration: 6000,
          });
        }
      }

      // Check for escalated applications (for executives)
      if (currentCounts.escalated > previousCountsRef.current.escalated) {
        const newCount = currentCounts.escalated - previousCountsRef.current.escalated;
        if (userRole === "executive" || userRole === "admin") {
          toast.warning(`⬆️ ${newCount} application(s) escalated for executive review`, {
            description: "High-value loans require your approval",
            duration: 8000,
          });
        }
      }

      // Update counts
      previousCountsRef.current = currentCounts;
      setNewApplicationsCount(currentCounts.pending);
      setEscalatedCount(currentCounts.escalated);
      setLastCheckTime(new Date());

    } catch (error) {
      console.error("Failed to check for updates:", error);
    }
  }, [token, userRole, enabled]);

  // Poll every 30 seconds
  useEffect(() => {
    if (!enabled) return;

    // Initial check
    checkForUpdates();

    // Set up polling interval
    const interval = setInterval(checkForUpdates, 30000);

    return () => clearInterval(interval);
  }, [checkForUpdates, enabled]);

  return {
    newApplicationsCount,
    escalatedCount,
    lastCheckTime,
    refresh: checkForUpdates,
  };
};

export default useBackOfficeNotifications;
